import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSettings } from '@/shared/hooks/useCloudData';

type PendingAction = (() => void) | null;

interface ClientLockContextValue {
  isLocked: boolean;
  isPinAvailable: boolean;
  lock: () => void;
  unlock: () => void;
  requirePin: (action: () => void, options?: { releaseLock?: boolean }) => void;
  _modal: {
    open: boolean;
    close: () => void;
    submit: (pin: string) => boolean;
  };
}

const ClientLockContext = createContext<ClientLockContextValue | undefined>(undefined);

export function ClientLockProvider({ children }: { children: React.ReactNode }) {
  const settings = useSettings();
  const [isLocked, setIsLocked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const pendingRef = useRef<PendingAction>(null);
  const releaseOnSuccessRef = useRef(false);

  const isPinAvailable = !!settings.pinEnabled && !!settings.pinCode;

  useEffect(() => {
    if (!isPinAvailable && isLocked) {
      setIsLocked(false);
      setModalOpen(false);
      pendingRef.current = null;
    }
  }, [isPinAvailable, isLocked]);

  useEffect(() => {
    if (!isLocked) return;
    window.history.pushState({ __clientLock: true }, '');
    const handlePopState = () => {
      window.history.pushState({ __clientLock: true }, '');
      releaseOnSuccessRef.current = true;
      pendingRef.current = null;
      setModalOpen(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isLocked]);

  const lock = useCallback(() => {
    if (!isPinAvailable) return;
    setIsLocked(true);
  }, [isPinAvailable]);

  const unlock = useCallback(() => {
    if (!isLocked) return;
    pendingRef.current = null;
    releaseOnSuccessRef.current = true;
    setModalOpen(true);
  }, [isLocked]);

  const requirePin = useCallback(
    (action: () => void, options?: { releaseLock?: boolean }) => {
      if (!isLocked) { action(); return; }
      pendingRef.current = action;
      releaseOnSuccessRef.current = options?.releaseLock ?? false;
      setModalOpen(true);
    },
    [isLocked],
  );

  const submit = useCallback(
    (pin: string) => {
      if (pin !== settings.pinCode) return false;
      const action = pendingRef.current;
      pendingRef.current = null;
      setModalOpen(false);
      if (releaseOnSuccessRef.current) {
        releaseOnSuccessRef.current = false;
        setIsLocked(false);
      }
      if (action) action();
      return true;
    },
    [settings.pinCode],
  );

  const close = useCallback(() => {
    pendingRef.current = null;
    releaseOnSuccessRef.current = false;
    setModalOpen(false);
  }, []);

  const value = useMemo<ClientLockContextValue>(
    () => ({
      isLocked,
      isPinAvailable,
      lock,
      unlock,
      requirePin,
      _modal: { open: modalOpen, close, submit },
    }),
    [isLocked, isPinAvailable, lock, unlock, requirePin, modalOpen, close, submit],
  );

  return <ClientLockContext.Provider value={value}>{children}</ClientLockContext.Provider>;
}

export function useClientLock(): ClientLockContextValue {
  const ctx = useContext(ClientLockContext);
  if (!ctx) throw new Error('useClientLock must be used within a ClientLockProvider');
  return ctx;
}

export function useRequirePin() {
  const { requirePin } = useClientLock();
  return requirePin;
}
